import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Plus, Search, Globe, User as UserIcon, Pencil, Trash2, Copy, Archive, FileText } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { reportTemplatesApi, TEMPLATE_CATEGORIES, categoryLabel, type Template } from '../lib/reportTemplatesApi';
import TemplateEditModal from '../components/TemplateEditModal';
import TemplateDetailModal from '../components/TemplateDetailModal';
import CreateTemplateModal from '../components/CreateTemplateModal';
import DeletedTemplatesModal from '../components/DeletedTemplatesModal';

type Tab = 'system' | 'user';

export default function Templates() {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('user');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const [detail, setDetail] = useState<Template | null>(null);
  const [editing, setEditing] = useState<Template | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Template | null>(null);
  const [deletedOpen, setDeletedOpen] = useState(false);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-Link ?edit=<id> — kommt von "Als Vorlage speichern" im Editor (Link "Vorlage bearbeiten").
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || templates.length === 0) return;
    const t = templates.find((x) => x.id === Number(editId));
    if (t) {
      setTab(t.scope);
      setEditing(t);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportTemplatesApi.list();
      setTemplates(data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Laden fehlgeschlagen';
      if (/forbidden|403|freigeschaltet/i.test(msg)) {
        navigate('/');
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isOwnerOf = (t: Template) => t.createdByUserId === user?.id;
  const canEdit = (t: Template) => !!user?.isAdmin || (t.scope === 'user' && isOwnerOf(t));
  const canDelete = canEdit;

  // Spans mit role="button" bekommen Enter/Space nicht automatisch (anders als <button>) —
  // ohne das hier sind die Icon-Aktionen per Tastatur nicht erreichbar.
  const onKeyActivate = (fn: () => void) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };

  const filtered = useMemo(() => {
    let list = templates.filter((t) => t.scope === tab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          categoryLabel(t.category).toLowerCase().includes(q),
      );
    }
    if (category) {
      list = list.filter((t) => t.category === category);
    }
    return list;
  }, [templates, tab, search, category]);

  const systemCount = templates.filter((t) => t.scope === 'system').length;
  const userCount = templates.filter((t) => t.scope === 'user').length;

  const bulletCount = (t: Template) =>
    t.items.reduce((sum, it) => {
      try {
        const v = JSON.parse(it.valuesJson);
        return sum + (Array.isArray(v) ? v.length : 0);
      } catch {
        return sum;
      }
    }, 0);

  const handleDelete = async (t: Template) => {
    setError(null);
    try {
      await reportTemplatesApi.remove(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      setDetail(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleDuplicate = async (t: Template) => {
    setError(null);
    try {
      const copy = await reportTemplatesApi.duplicate(t.id);
      setTemplates((prev) => [...prev, copy]);
      setDetail(null);
      setTab('user');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Duplizieren fehlgeschlagen');
    }
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const input = darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300';

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-lg">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Vorlagen</h1>
            <p className={`text-sm ${muted}`}>Bullet-Listen-Vorlagen für Berichts-Sections</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDeletedOpen(true)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
          >
            <Archive className="w-4 h-4" />
            Gelöschte Vorlagen
          </button>
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            <Plus className="w-4 h-4" />
            Neue Vorlage
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        {(['user', 'system'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t
                ? 'border-blue-600 text-blue-600'
                : `border-transparent ${muted} hover:text-slate-700 dark:hover:text-slate-200`
            }`}
          >
            {t === 'system' ? `Systemvorlagen (${systemCount})` : `Meine Vorlagen (${userCount})`}
          </button>
        ))}
      </div>

      {/* Suche + Kategorie-Filter */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${muted}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name oder Beschreibung durchsuchen…"
            className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm ${input}`}
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className={`px-3 py-2 border rounded-lg text-sm ${input}`}
        >
          <option value="">Alle Kategorien</option>
          {TEMPLATE_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{error}</div>
      )}

      {/* Liste */}
      {loading ? (
        <p className={`text-sm ${muted}`}>Lädt…</p>
      ) : filtered.length === 0 ? (
        <p className={`text-sm italic ${muted}`}>
          {templates.filter((t) => t.scope === tab).length === 0
            ? tab === 'system'
              ? 'Keine Systemvorlagen vorhanden.'
              : 'Du hast noch keine eigenen Vorlagen. Lege eine neue an oder speichere einen Bericht als Vorlage.'
            : 'Keine Vorlagen gefunden.'}
        </p>
      ) : (
        <div className={`rounded-xl border divide-y ${card} ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
          {filtered.map((t) => (
            <button
              key={t.id}
              onClick={() => setDetail(t)}
              className={`w-full flex items-center justify-between gap-3 p-4 text-left transition-colors ${darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {t.scope === 'system' ? (
                  <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
                ) : (
                  <UserIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{t.name}</p>
                  {t.description && <p className={`text-xs truncate ${muted}`}>{t.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`hidden sm:inline-block text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                  {categoryLabel(t.category)}
                </span>
                <span className={`hidden sm:inline text-xs ${muted}`}>{bulletCount(t)} Bullets</span>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDuplicate(t)}
                    onKeyDown={onKeyActivate(() => handleDuplicate(t))}
                    title="Duplizieren"
                    aria-label="Duplizieren"
                    className={`p-1.5 rounded cursor-pointer ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </span>
                  {canEdit(t) && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setEditing(t)}
                      onKeyDown={onKeyActivate(() => setEditing(t))}
                      title="Bearbeiten"
                      aria-label="Bearbeiten"
                      className={`p-1.5 rounded cursor-pointer ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </span>
                  )}
                  {canDelete(t) && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setConfirmDelete(t)}
                      onKeyDown={onKeyActivate(() => setConfirmDelete(t))}
                      title="Löschen"
                      aria-label="Löschen"
                      className="p-1.5 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail-Vorschau */}
      {detail && (
        <TemplateDetailModal
          template={detail}
          open
          canEdit={canEdit(detail)}
          canDelete={canDelete(detail)}
          onClose={() => setDetail(null)}
          onEdit={() => {
            setEditing(detail);
            setDetail(null);
          }}
          onDuplicate={() => handleDuplicate(detail)}
          onDelete={() => setConfirmDelete(detail)}
        />
      )}

      {/* Bearbeiten */}
      {editing && (
        <TemplateEditModal
          template={editing}
          open
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setEditing(null);
          }}
        />
      )}

      {/* Neue Vorlage */}
      <CreateTemplateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setTemplates((prev) => [...prev, created]);
          setCreating(false);
          setTab('user');
          setEditing(created);
        }}
      />

      {/* Gelöschte Vorlagen */}
      <DeletedTemplatesModal
        open={deletedOpen}
        onClose={() => setDeletedOpen(false)}
        onRestored={(restored) => setTemplates((prev) => [...prev, restored])}
      />

      {/* Löschen-Bestätigung */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`rounded-xl border shadow-xl p-6 max-w-sm w-full ${card}`}>
            <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">Vorlage löschen?</h3>
            <p className={`text-sm mb-6 ${muted}`}>
              Möchtest du „{confirmDelete.name}" wirklich löschen? Bereits damit verbundene Berichte bleiben unverändert — du kannst die Vorlage danach über „Gelöschte Vorlagen" wiederherstellen.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className={`px-4 py-2 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700 text-white' : 'border-slate-300 hover:bg-slate-50 text-slate-700'}`}
              >
                Abbrechen
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
